import type { VestiaireCatalogNodeLike } from '@/features/productSearch/vestiaire/catalogTree';
import { buildVestiaireCriteria, vestiaireFilterToForm } from '@/features/productSearch/vestiaire/vestiaireCriteria';
import { type VestiaireUrlLanguage, buildVestiaireSearchUrl } from '@/features/productSearch/vestiaire/vestiaireUrl';
import type { AnalysisPlatform, AnalysisType } from '@/shared/api/types/generated';
import { isVestiaireAnalysis } from '@/shared/utils/analysis/platform';

// ============================================================================
// Lien externe « Voir sur Vestiaire Collective » du header d'analyse. L'API stocke
// `pageUrl` à la création comme une simple recherche par mots-clés (`vestiaireCatalogPageUrl`,
// apps/api/src/vestiaire/utils/vestiaire-urls.ts) : pour une analyse VC de CATALOGUE, le lien
// réel se recalcule ici depuis le `vestiaireFilter` stocké, avec le générateur du formulaire
// (buildVestiaireSearchUrl) — mais SEULEMENT si l'arbre des catégories, les pays et les matières sont
// disponibles (aucun `null`) : sans l'arbre, `vestiaireFilterToForm` ne peut pas retrouver les chemins de catégorie et
// l'URL générée omettrait silencieusement ce critère, et sans pays ni matières leurs ids ne pourraient
// pas être écrits ; d'où le repli sur `pageUrl` (jamais de lien dégradé sans avertissement). Une analyse VC de PROFIL garde `pageUrl` (URL du profil vendeur,
// correcte dès la création). Vinted : inchangé, toujours `pageUrl`.
//
// `AnalysisType` (généré depuis le schéma GraphQL) ne vaut que 'catalog' | 'profile' |
// '%future added value' — une valeur future est traitée comme un profil (on ne recalcule QUE le
// cas catalogue connu). ⚠️ Ne pas confondre avec le type FRONT du même nom
// (`shared/types/analysis/enums.ts`, 'product' | 'vendor' | 'public', porté par
// `AnalysisHeaderData` via `mapBackendAnalysisTypeToFrontendType`) : à l'appel depuis
// AnalysisDetailPage.tsx, `analysis.type` doit être reconverti ('vendor' → 'profile', sinon
// 'catalog') avant d'être passé ici.
// ============================================================================

interface AnalysisExternalUrlSource {
  platform: AnalysisPlatform;
  type: AnalysisType;
  // Optionnel (pas seulement `string`) : AnalysisHeaderData (front) porte `pageUrl` en optionnel,
  // bien que le schéma GraphQL le rende non-nullable.
  pageUrl?: string;
  vestiaireFilter?: Record<string, unknown> | null;
}

interface AnalysisExternalUrlContext {
  language: VestiaireUrlLanguage;
  /**
   * Arbre des catégories VC — `null` quand il n'est PAS DISPONIBLE (chargement en cours, requête en
   * échec, ou tout autre état que « chargé ») : on ne distingue pas la raison, seulement l'absence,
   * pour ne jamais générer un lien dont les catégories seraient silencieusement absentes faute
   * d'arbre à consulter.
   */
  tree: VestiaireCatalogNodeLike[] | null;
  /** Codes ISO à 2 lettres des pays VC — `null` quand la liste n'est pas disponible (même règle que `tree`). */
  countryIsoCodes: ReadonlyMap<number, string> | null;
  /** Niveau VC (0/1) de chaque matière — `null` quand la liste n'est pas disponible (même règle que `tree`). */
  materialLevels: ReadonlyMap<number, number> | null;
}

/** URL du lien externe d'une analyse : VC catalogue ⇒ URL générée depuis le filtre stocké ; sinon `pageUrl`. */
export const analysisExternalUrl = (
  analysis: AnalysisExternalUrlSource,
  context: AnalysisExternalUrlContext,
): string | undefined => {
  if (
    !isVestiaireAnalysis(analysis) ||
    analysis.type !== 'catalog' ||
    !analysis.vestiaireFilter ||
    context.tree === null ||
    context.countryIsoCodes === null ||
    context.materialLevels === null
  ) {
    return analysis.pageUrl;
  }
  const form = vestiaireFilterToForm(analysis.vestiaireFilter, context.tree);
  const criteria = buildVestiaireCriteria(form);
  return buildVestiaireSearchUrl(criteria, context.language, context.countryIsoCodes, context.materialLevels).url;
};
